import type { Config, Mixpanel } from 'mixpanel-browser';

const productionToken = '8cefb57a951e06c6323e5b03603641ab';
const token = import.meta.env.VITE_MIXPANEL_TOKEN || productionToken;
const enabled = import.meta.env.VITE_MIXPANEL_ENABLED !== 'false'
  && import.meta.env.MODE !== 'test';
let sdkPromise: Promise<Mixpanel | undefined> | undefined;
let operations = Promise.resolve();
let identity: string | undefined;
let viewKey: string | undefined;
let replayStarted = false;

function sdk() {
  if (!enabled) return Promise.resolve(undefined);
  sdkPromise ??= import('mixpanel-browser').then(({ default: mixpanel }) => {
    const config: Partial<Config> & Record<string, unknown> = {
      api_host: 'https://api-eu.mixpanel.com',
      persistence: 'localStorage',
      autocapture: false, // Explicit capture avoids collecting query strings, labels, or form values.
      track_pageview: false,
      ip: true,
      save_referrer: false,
      stop_utm_persistence: true,
      record_sessions_percent: 0, // Start after identity is established; don't record authentication.
      record_mask_all_text: false,
      record_mask_all_inputs: false,
      record_block_selector: 'video, canvas, .mp-block, [data-analytics-private]',
      record_console: false,
      remote_settings_mode: 'disabled',
      property_blacklist: ['$current_url', '$referrer', '$initial_referrer'],
    };
    mixpanel.init(token, config);
    mixpanel.register({ platform: 'web', application: 'demand_manager', environment: import.meta.env.PROD ? 'production' : 'development' });
    return mixpanel;
  }).catch(() => {
    console.warn('Mixpanel could not initialize; the app will continue without tracking.');
    return undefined;
  });
  return sdkPromise;
}

function enqueue(action: (mixpanel: Mixpanel) => void | Promise<void>) {
  operations = operations.then(async () => {
    const mixpanel = await sdk();
    if (mixpanel) await action(mixpanel);
  }).catch(() => { /* Analytics must never interrupt application behavior. */ });
}

async function stopReplay(mixpanel: Mixpanel) {
  try {
    await mixpanel.stop_session_recording();
  } catch {
    console.warn('Mixpanel recorder could not stop cleanly; resetting user identity.');
  }
  replayStarted = false;
}

export function identifyUser(userId: string, email?: string) {
  enqueue(async mixpanel => {
    const existingSession = identity === userId && replayStarted;
    if (identity && identity !== userId) {
      await stopReplay(mixpanel);
      mixpanel.reset();
    }
    mixpanel.identify(userId);
    mixpanel.register({ platform: 'web', application: 'demand_manager', environment: import.meta.env.PROD ? 'production' : 'development' });
    const profileEmail = email?.trim();
    mixpanel.people.set({ platform: 'web', ...(profileEmail ? { $email: profileEmail } : {}) });
    if (existingSession) return;
    identity = userId;
    try {
      await mixpanel.start_session_recording();
      replayStarted = true;
    } catch {
      replayStarted = false;
      console.warn('Mixpanel session replay could not start; analytics remains active.');
    }
    mixpanel.track('session_authenticated');
  });
}

export function resetUser() {
  enqueue(async mixpanel => {
    await stopReplay(mixpanel);
    if (!identity && !mixpanel.get_property('$user_id')) return;
    mixpanel.reset();
    mixpanel.register({ platform: 'web', application: 'demand_manager', environment: import.meta.env.PROD ? 'production' : 'development' });
    identity = undefined;
    viewKey = undefined;
  });
}

export function trackPageView(pageName: string, entityKey?: string | number | null) {
  enqueue(mixpanel => {
    const nextKey = `${pageName}:${entityKey ?? ''}`;
    if (viewKey === nextKey) return; // Includes React StrictMode's repeated effects.
    viewKey = nextKey;
    mixpanel.register({ page_name: pageName });
    mixpanel.track('page_viewed', { page_name: pageName });
  });
}

export function captureClick(event: MouseEvent) {
  const target = event.target instanceof Element ? event.target : undefined;
  if (!target || target.closest('[data-analytics-private], .mp-block')) return;
  const element = target.closest('button, a, input, select, textarea, [role="button"], [data-track-id]') ?? target;
  const tag = element.tagName.toLowerCase();
  const path: string[] = [];
  for (let node: Element | null = element; node && path.length < 6; node = node.parentElement) {
    const index = node.parentElement ? Array.from(node.parentElement.children).indexOf(node) + 1 : 1;
    path.unshift(`${node.tagName.toLowerCase()}:nth-child(${index})`);
  }
  // Collect displayed button labels only; never read input values or private subtrees.
  const isButton = tag === 'button' || element.getAttribute('role') === 'button';
  let buttonText: string | undefined;
  if (isButton) {
    const label = element.cloneNode(true) as HTMLElement;
    label.querySelectorAll('[data-analytics-private], .mp-block, input, textarea, select, script, style, [hidden], [aria-hidden="true"]').forEach(node => node.remove());
    const text = (label.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 200);
    if (text) buttonText = text;
  }
  const trackId = element.getAttribute('data-track-id');
  enqueue(mixpanel => mixpanel.track('element_clicked', {
    element_type: tag,
    ...(buttonText ? { button_text: buttonText } : {}),
    element_path: path.join(' > '),
    x: Math.round(event.clientX),
    y: Math.round(event.clientY),
    ...(trackId && /^[a-z][a-z0-9_]{0,63}$/.test(trackId) ? { element_name: trackId } : {}),
  }));
}
